import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, ShoppingBag, CalendarDays, Sparkles, ArrowRight, Star, ShieldCheck, Printer, MapPin, Phone, MessageCircle, Navigation } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import OpenBadge from "../components/OpenBadge";
import ProductCard from "../components/ProductCard";

const HERO_FALLBACK = "https://images.pexels.com/photos/8183842/pexels-photo-8183842.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const QUICK_ACTIONS = [
  { to: "/reparation", icon: Wrench, title: "Devis réparation", desc: "Estimation immédiate en 2 minutes", testid: "hero-repair-btn" },
  { to: "/catalogue", icon: ShoppingBag, title: "Acheter / Réserver", desc: "Téléphones & accessoires en stock", testid: "hero-catalog-btn" },
  { to: "/rendez-vous", icon: CalendarDays, title: "Prendre RDV", desc: "Un créneau rien qu'à vous", testid: "hero-rdv-btn" },
  { to: "/quiz", icon: Sparkles, title: "Trouver mon téléphone", desc: "Le quiz qui vous conseille", testid: "hero-quiz-btn" },
];

export default function Home() {
  const { settings, waLink } = useSettings();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { limit: 4, dispo: "available" } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!settings) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: settings.store_name,
      description: "Vente de téléphones et accessoires, réparation de smartphones.",
      telephone: settings.phone || undefined,
      email: settings.email || undefined,
      address: settings.address ? { "@type": "PostalAddress", streetAddress: settings.address, addressLocality: settings.city || undefined, addressCountry: "FR" } : undefined,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-localbusiness";
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => document.getElementById("ld-localbusiness")?.remove();
  }, [settings]);

  const heroImg = settings?.hero_image ? fileUrl(settings.hero_image) : HERO_FALLBACK;
  const hasWhatsApp = Boolean((settings?.whatsapp_number || "").trim());

  return (
    <div data-testid="home-page">
      <section className="container-oxy pt-6 sm:pt-10">
        <div className="overflow-hidden rounded-3xl bg-ink text-white shadow-2xl">
          <div className="grid items-stretch md:grid-cols-2">
            <div className="flex flex-col justify-center gap-6 p-6 sm:p-10 lg:p-14">
              <div className="flex flex-wrap items-center gap-3">
                <span className="eyebrow !text-gold">{settings?.tagline || "Boutique & atelier téléphonie"}</span>
                <OpenBadge />
              </div>
              <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Votre téléphone mérite <span className="text-gold">l'excellence</span>.
              </h1>
              <p className="max-w-md text-base leading-relaxed text-slate-300">
                Vente de téléphones & accessoires, réparation express avec devis gratuit, et un vrai service de proximité.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/reparation" data-testid="hero-cta-quote" className="btn-gold">Devis réparation <ArrowRight className="h-4 w-4" /></Link>
                <Link to="/catalogue" data-testid="hero-cta-catalog" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 font-display font-semibold text-white transition-all duration-300 hover:border-gold hover:text-gold">Voir le catalogue</Link>
              </div>
            </div>
            <div className="relative min-h-[240px] md:min-h-[420px]">
              <img src={heroImg} alt={`Boutique ${settings?.store_name || "Oxyservices"}`} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent md:bg-gradient-to-r md:from-ink md:via-ink/20 md:to-transparent" />
            </div>
          </div>
        </div>
      </section>

      <section className="container-oxy mt-8 sm:mt-12">
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          {QUICK_ACTIONS.map((a, i) => {
            const Icon = a.icon;
            return (
              <Link key={a.to} to={a.to} data-testid={a.testid}
                className="card-oxy group flex flex-col gap-3 p-5 sm:p-6 animate-fade-up"
                style={{ animationDelay: `${i * 80}ms` }}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-gold transition-transform duration-300 group-hover:scale-110">
                  <Icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-base font-bold text-slate-900 sm:text-lg">{a.title}</h3>
                  <p className="mt-1 text-xs text-slate-500 sm:text-sm">{a.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {products.length > 0 && (
        <section className="container-oxy mt-14 sm:mt-20">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="eyebrow">En boutique</p>
              <h2 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Nos produits du moment</h2>
            </div>
            <Link to="/catalogue" data-testid="see-catalog-link" className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-gold-dark hover:text-gold sm:inline-flex">
              Tout voir <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          <Link to="/catalogue" className="btn-ink mt-6 w-full sm:hidden">Voir tout le catalogue</Link>
        </section>
      )}

      <section className="container-oxy mt-14 sm:mt-20">
        <div className="rounded-3xl border border-amber-500/20 bg-gold-soft p-6 sm:p-10">
          <div className="grid gap-8 md:grid-cols-3">
            <div className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><Wrench className="h-5 w-5" /></span>
              <div>
                <h3 className="font-display font-bold text-slate-900">Réparation express</h3>
                <p className="mt-1 text-sm text-slate-600">Écran, batterie, caméra, connecteur… la plupart des réparations en moins d'1h30.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><ShieldCheck className="h-5 w-5" /></span>
              <div>
                <h3 className="font-display font-bold text-slate-900">Diagnostic honnête</h3>
                <p className="mt-1 text-sm text-slate-600">Estimation indicative en ligne, tarif confirmé après diagnostic en boutique.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-ink text-gold"><Printer className="h-5 w-5" /></span>
              <div>
                <h3 className="font-display font-bold text-slate-900">Impression & démarches</h3>
                <p className="mt-1 text-sm text-slate-600">Impressions, photocopies et aide aux démarches administratives en boutique.</p>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/services" data-testid="home-services-link" className="btn-ink">Découvrir tous nos services</Link>
          </div>
        </div>
      </section>

      {reviews.length > 0 && (
        <section className="container-oxy mt-14 sm:mt-20" data-testid="reviews-section">
          <p className="eyebrow text-center">Ils nous font confiance</p>
          <h2 className="mt-1 text-center font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Avis de nos clients</h2>
          <div className="mt-8 grid gap-4 sm:gap-6 md:grid-cols-3">
            {reviews.map((r) => (
              <div key={r.id} data-testid={`review-${r.id}`} className="card-oxy p-5 sm:p-6">
                <div className="flex items-center gap-1" aria-label={`${r.rating} étoiles`}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-gold text-gold" : "text-slate-300"}`} />
                  ))}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">{r.text}</p>
                <p className="mt-4 text-sm font-semibold text-slate-900">
                  {r.author}
                  {r.demo && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Exemple</span>}
                </p>
              </div>
            ))}
          </div>
          {settings?.google_reviews_url && (
            <div className="mt-8 text-center">
              <a href={settings.google_reviews_url} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-btn" className="btn-outline-oxy">
                <Star className="h-4 w-4 text-gold" /> Voir nos avis Google
              </a>
            </div>
          )}
        </section>
      )}

      <section className="container-oxy mt-14 sm:mt-20">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="card-oxy p-6 sm:p-8">
            <p className="eyebrow">Nous trouver</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-slate-900">Passez nous voir</h2>
            <div className="mt-5 space-y-3 text-sm text-slate-700">
              {settings?.address && (
                <p className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold-dark" />{settings.address}{settings.city ? `, ${settings.city}` : ""}</p>
              )}
              {settings?.phone && (
                <a href={`tel:${settings.phone}`} className="flex items-center gap-3 hover:text-gold-dark"><Phone className="h-5 w-5 shrink-0 text-gold-dark" />{settings.phone}</a>
              )}
              <div className="pt-1"><OpenBadge /></div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {settings?.phone && (
                <a href={`tel:${settings.phone}`} data-testid="home-call-btn" className="btn-ink !px-5 !py-2.5 text-sm"><Phone className="h-4 w-4" /> Appeler</a>
              )}
              {hasWhatsApp && (
                <a href={waLink("Bonjour Oxyservices, j'ai une question.")} target="_blank" rel="noopener noreferrer" data-testid="home-whatsapp-btn" className="btn-outline-oxy !px-5 !py-2.5 text-sm"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
              )}
              {settings?.maps_address && (
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(settings.maps_address)}`} target="_blank" rel="noopener noreferrer" data-testid="home-directions-btn" className="btn-outline-oxy !px-5 !py-2.5 text-sm"><Navigation className="h-4 w-4" /> Itinéraire</a>
              )}
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-amber-500/20 shadow-sm">
            {settings?.maps_address ? (
              <iframe title="Plan d'accès Oxyservices" data-testid="home-map" className="h-full min-h-[300px] w-full" loading="lazy"
                src={`https://www.google.com/maps?q=${encodeURIComponent(settings.maps_address)}&output=embed`} />
            ) : (
              <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 bg-slate-100 text-slate-400">
                <MapPin className="h-8 w-8" />
                <p className="text-sm">La carte s'affichera une fois l'adresse configurée dans l'admin.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
